import { type IntranetColleague } from '@gb-ticket/shared-types';
import { Loader2Icon, PhoneIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ListPagination } from '@/components/list-pagination';
import { usePageTitle } from '@/hooks/use-page-title';
import { useMe } from '@/platform/auth/use-me';
import { useVoice } from '@/platform/voice/use-voice';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ColleagueChatButton } from '@/products/intranet/components/colleague-chat-button';
import {
  ColleagueNameButton,
  colleagueVisibleName,
} from '@/products/intranet/components/colleague-name-button';
import { ColleaguePortrait } from '@/products/intranet/features/intranet/colleague-portrait';
import { useIntranetColleagues } from '@/products/intranet/features/intranet/use-intranet-directory';

const SEARCH_DEBOUNCE_MS = 300;

function joinParts(parts: (string | null)[]): string {
  return parts
    .map((part) => part?.trim() ?? '')
    .filter((part) => part.length > 0)
    .join(' · ');
}

export function ColleaguesPage() {
  usePageTitle('Kollegen');
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [page, setPage] = useState(1);
  const filterKey = `${q}\0${departmentId}`;
  const [pageKey, setPageKey] = useState(filterKey);
  if (pageKey !== filterKey) {
    setPageKey(filterKey);
    setPage(1);
  }
  const currentPage = pageKey === filterKey ? page : 1;
  const colleagues = useIntranetColleagues({
    q: q || undefined,
    departmentId: departmentId || undefined,
    page: currentPage,
    pageSize: 25,
  });
  const items = colleagues.data?.items ?? [];
  const departments = colleagues.data?.departments ?? [];

  useEffect(() => {
    const handle = window.setTimeout(() => setQ(draft.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft]);

  return (
    <section className="min-w-0 space-y-6">
      <PlatformPageHeader
        title="Kollegen"
        description="Aktive Mitarbeiterinnen und Mitarbeiter. Telefon nur, wenn selbst hinterlegt."
      />

      <div className="grid min-w-0 gap-3 sm:grid-cols-2">
        <div className="min-w-0 space-y-2">
          <Label htmlFor="intranet-colleagues-q">Suche</Label>
          <Input
            id="intranet-colleagues-q"
            value={draft}
            placeholder="Name oder Stellenbezeichnung"
            onChange={(event) => setDraft(event.target.value)}
          />
        </div>
        <div className="min-w-0 space-y-2">
          <Label htmlFor="intranet-colleagues-dept">Abteilung</Label>
          <Select
            value={departmentId || 'none'}
            onValueChange={(value) => setDepartmentId(value === 'none' ? '' : value)}
          >
            <SelectTrigger id="intranet-colleagues-dept" className="w-full max-w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Alle Abteilungen</SelectItem>
              {departments.map((department) => (
                <SelectItem key={department.id} value={department.id}>
                  {department.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {colleagues.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Verzeichnis wird geladen …
        </p>
      ) : colleagues.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Das Verzeichnis konnte nicht geladen werden.
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine Treffer.</p>
      ) : (
        <ul className="grid min-w-0 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((person) => (
            <li key={person.id} className="min-w-0">
              <ColleagueCard person={person} />
            </li>
          ))}
        </ul>
      )}
      {colleagues.data ? (
        <ListPagination
          page={colleagues.data.page}
          pageSize={colleagues.data.pageSize}
          total={colleagues.data.total}
          onPageChange={setPage}
        />
      ) : null}
    </section>
  );
}

function ColleagueCard({ person }: { person: IntranetColleague }) {
  const voice = useVoice();
  const me = useMe();
  const isSelf = me.data?.id === person.id;
  const roleLine = joinParts([person.jobTitle, person.departmentName]);
  const placeLine = joinParts([person.siteName]);

  return (
    <Card className="h-full min-w-0 gap-0 overflow-hidden py-0">
      <ColleagueNameButton
        userId={person.id}
        displayName={person.displayName}
        className="flex w-full min-w-0 flex-1 items-start gap-3 px-4 py-4 text-left hover:bg-accent/40 hover:no-underline"
      >
        <ColleaguePortrait
          displayName={person.displayName}
          profilePhotoUrl={person.profilePhotoUrl}
        />
        <span className="min-w-0 flex-1 space-y-1">
          <span className="block truncate text-base leading-none font-semibold">
            {colleagueVisibleName(person.displayName, voice)}
          </span>
          <span className="block min-h-5 truncate text-sm font-normal text-muted-foreground">
            {roleLine}
          </span>
          <span className="block min-h-5 truncate text-sm font-normal text-muted-foreground">
            {placeLine}
          </span>
        </span>
      </ColleagueNameButton>
      <div className="mt-auto flex min-w-0 flex-col gap-2 border-t px-4 py-3 sm:h-14 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:py-0">
        {person.phone ? (
          <a
            href={`tel:${person.phone}`}
            className="flex min-w-0 items-center gap-1.5 text-sm hover:underline"
          >
            <PhoneIcon className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">{person.phone}</span>
          </a>
        ) : (
          <span className="hidden sm:block" />
        )}
        {isSelf ? null : (
          <ColleagueChatButton
            userId={person.id}
            reachesViaTeams={person.reachesViaTeams}
            className="w-full shrink-0 sm:w-auto"
          />
        )}
      </div>
    </Card>
  );
}
